import type { Art, StatusMap, Technique, Collection } from '../types';
import { isValidArt, sortArts } from '../data/arts';

let _uid: string | null = null;

/** Set the current user ID. Call on auth state change. */
export function setStorageUser(uid: string | null): void {
  _uid = uid;
}

/** Returns a user-scoped localStorage key. */
export function userKey(base: string): string {
  return _uid ? `${base}:${_uid}` : base;
}

export function loadStatusMap(): StatusMap {
  try {
    const raw = localStorage.getItem(userKey('gnosis-status'));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveStatusMap(map: StatusMap): void {
  localStorage.setItem(userKey('gnosis-status'), JSON.stringify(map));
}

export function loadCustomTechniques(): Technique[] {
  try {
    const raw = localStorage.getItem(userKey('gnosis-custom-techniques'));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveCustomTechniques(techniques: Technique[]): void {
  localStorage.setItem(userKey('gnosis-custom-techniques'), JSON.stringify(techniques));
}

/** The grappling arts the user chose in the "Show me…" picker. An empty array
 *  means they haven't picked yet (first run) and should see the full-screen
 *  picker. Reads fall back to the pre-multi-select `gnosis-art` single value so
 *  existing users aren't sent back through the gate. */
export function loadArts(): Art[] {
  try {
    const raw = localStorage.getItem(userKey('gnosis-arts'));
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) return sortArts(parsed.filter(isValidArt));
    }
    const legacy = localStorage.getItem(userKey('gnosis-art'));
    return isValidArt(legacy) ? [legacy] : [];
  } catch {
    return [];
  }
}

export function saveArts(arts: Art[]): void {
  localStorage.setItem(userKey('gnosis-arts'), JSON.stringify(sortArts(arts)));
  // Drop the legacy key so it can't resurrect a stale single-art choice.
  localStorage.removeItem(userKey('gnosis-art'));
}

export function loadCollections(): Collection[] {
  try {
    const raw = localStorage.getItem(userKey('gnosis-collections'));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveCollections(collections: Collection[]): void {
  localStorage.setItem(userKey('gnosis-collections'), JSON.stringify(collections));
}
